import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ResponsiveDataList from "../../components/ResponsiveDataList/ResponsiveDataList";
import { InstallationsUserInstallation } from "../../services/services";
import PersianDate from "../../components/PersianDate/PersianDate";

const HistoryInstall = ({ id, isMobile }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(11);

  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["InstallationsUserInstallation", id, pageNumber, pageSize],
    queryFn: () =>
      InstallationsUserInstallation({
        userId: id,
        pageNumber,
        pageSize,
      }),
    select: (res) => res,
    staleTime: 60 * 60000,
    retry: false,
    enabled: !!id,
  });

  const allRecords = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const openModal = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedRow(null), 120);
  };

  // ESC close + جلوگیری از اسکرول وقتی مودال بازه
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && isModalOpen) closeModal();
    };
    window.addEventListener("keydown", onKeyDown);

    if (isModalOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKeyDown);
        document.body.style.overflow = prevOverflow;
      };
    }

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen]);

  const ChargedBadge = ({ charged }) => (
    <span style={charged ? styles.badgeSuccess : styles.badgeDanger}>
      {charged ? "دریافت شده" : "دریافت نشده"}
    </span>
  );

  const columns = useMemo(
    () => [
      { field: "id", header: " شناسه نصب ", style: { textAlign: "right" } },
      { field: "serial", header: "سریال محصول", style: { textAlign: "right" } },
      {
        field: "code",
        header: " کد روی کارت خانه ستارگان ",
        style: { textAlign: "right" },
      },
      {
        field: "installDate",
        header: " تاریخ نصب ",
        body: (rowData) => <PersianDate date={rowData.installDate} />,
        sortable: true,
        style: { textAlign: "right" },
      },
      {
        field: "installProvince",
        header: "  استان ",
        style: { textAlign: "right" },
      },
      { field: "installCity", header: "  شهر ", style: { textAlign: "right" } },
      {
        field: "installiationProductName",
        header: "نام محصول",
        style: { textAlign: "right" },
      },
      {
        field: "quality",
        header: " امتیاز داده شده ",
        style: { textAlign: "right" },
      },
      {
        field: "suggestText",
        header: " پیام داده شده ",
        style: { textAlign: "right" },
      },
      {
        field: "isSimCharged",
        header: " وضعیت دریافت شارژ اعتباری  ",
        style: { textAlign: "right" },
        body: (rowData) => <ChargedBadge charged={!!rowData?.isSimCharged} />,
      },
    ],
    []
  );

  const itemTemplate = (item) => (
    <div
      key={item.id}
      className="rounded shadow-sm mb-3 p-3"
      style={{
        backgroundColor: "#f9f9f9",
        border: "1px solid #eee",
        cursor: "pointer",
      }}
      title="برای مشاهده جزئیات کلیک کنید"
    >
      <div className="mb-2 d-flex justify-content-between">
        <span className="text-muted">شناسه نصب :</span>
        <span className="fw-bold farsi-monospace-digit">{item.id}</span>
      </div>

      <div className="mb-1">
        <strong>سریال محصول:</strong> {item.serial}
      </div>
      <div className="mb-1">
        <strong> کد روی کارت خانه ستارگان :</strong> {item.code}
      </div>
      <div className="mb-1">
        <strong>تاریخ نصب:</strong> <PersianDate date={item.installDate} />
      </div>
      <div className="mb-1">
        <strong>استان :</strong> {item.installProvince}
      </div>
      <div className="mb-1">
        <strong>شهر :</strong> {item.installCity}
      </div>
      <div className="mb-1">
        <strong>نام محصول:</strong> {item.installiationProductName}
      </div>
      <div className="mb-1">
        <strong>امتیاز داده شده :</strong> {item.quality}
      </div>
      <div className="mb-1">
        <strong> پیام داده شده :</strong> {item.suggestText}
      </div>
      <div
        className="mb-1"
        style={{ display: "flex", gap: 8, alignItems: "center" }}
      >
        <strong> وضعیت دریافت شارژ اعتباری :</strong>
        <ChargedBadge charged={!!item?.isSimCharged} />
      </div>
    </div>
  );

  const DetailsModal = ({ open, row, onClose }) => {
    if (!open || !row) return null;

    const modalNode = (
      <div
        style={styles.backdrop}
        onMouseDown={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          style={styles.modal}
          onMouseDown={(e) => e.stopPropagation()}
          className="farsi-monospace-digit"
        >
          <div style={styles.modalHeader}>
            <div>
              <div style={styles.modalTitle}>جزئیات نصب</div>
              <div style={styles.modalSubtitle}>
                شناسه نصب: <span style={{ fontWeight: 800 }}>{row.id}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={styles.closeBtn}
              aria-label="بستن"
            >
              ×
            </button>
          </div>

          <div style={styles.modalBody}>
            <div style={styles.grid}>
              <div style={styles.field}>
                <div style={styles.label}>سریال محصول</div>
                <div style={styles.value}>{row.serial ?? "-"}</div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>کد روی کارت خانه ستارگان</div>
                <div style={styles.value}>{row.code ?? "-"}</div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>تاریخ نصب</div>
                <div style={styles.value}>
                  <PersianDate date={row.installDate} />
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>استان</div>
                <div style={styles.value}>{row.installProvince ?? "-"}</div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>شهر</div>
                <div style={styles.value}>{row.installCity ?? "-"}</div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>نام محصول</div>
                <div style={styles.value}>
                  {row.installiationProductName ?? "-"}
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>امتیاز داده شده</div>
                <div style={styles.value}>{row.quality ?? "-"}</div>
              </div>

              <div style={{ ...styles.field, gridColumn: "1 / -1" }}>
                <div style={styles.label}>پیام داده شده</div>
                <div style={styles.valueLong}>{row.suggestText ?? "-"}</div>
              </div>

              <div style={{ ...styles.field, gridColumn: "1 / -1" }}>
                <div style={styles.label}>وضعیت دریافت شارژ اعتباری</div>
                <div style={styles.value}>
                  <ChargedBadge charged={!!row?.isSimCharged} />
                </div>
              </div>
            </div>

            <div style={styles.hint}>
              برای بستن، بیرون پاپ‌آپ کلیک کنید یا کلید <b>ESC</b> را بزنید.
            </div>
          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.primaryBtn}>
              بستن
            </button>
          </div>

          {/* keyframes داخل همین فایل */}
          <style>
            {`
              @keyframes historyInstallPop {
                from { transform: translateY(10px) scale(0.98); opacity: 0; }
                to   { transform: translateY(0) scale(1); opacity: 1; }
              }
            `}
          </style>
        </div>
      </div>
    );

    // ✅ مهم: Portal به body => دیگر زیر هیچ بخشی نمی‌رود
    return createPortal(modalNode, document.body);
  };

  return (
    <div className="card mt-4" style={isMobile ? {} : { height: "650px" }}>
      <h5> لیست نصب های کاربر </h5>

      {allRecords?.length > 0 ? (
        <div className="background-dataTable">
          <ResponsiveDataList
            data={allRecords}
            columns={columns}
            itemTemplate={itemTemplate}
            dataViewHeader={<></>}
            className="farsi-monospace-digit"
            rows={pageSize}
            pageNumber={pageNumber}
            totalCount={totalCount}
            onPageChange={({ pageNumber: p, pageSize: s }) => {
              setPageNumber(p);
              setPageSize(s);
            }}
            onRowClick={(row) => openModal(row)}
          />
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "rgb(100, 100, 100)",
          }}
        >
          <h4>موردی یافت نشد!</h4>
        </div>
      )}

      <DetailsModal open={isModalOpen} row={selectedRow} onClose={closeModal} />
    </div>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)", // ✅ این خیلی مهمه
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 2147483647, // ✅ خیلی بالا برای اینکه زیر هیچ چیزی نره
  },
  modal: {
    width: "min(860px, 100%)",
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 18px 60px rgba(0,0,0,0.28)",
    overflow: "hidden",
    animation: "historyInstallPop 150ms ease-out",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 18px",
    background: "linear-gradient(180deg, #fafafa, #ffffff)",
    borderBottom: "1px solid #eee",
  },
  modalTitle: { fontSize: 16, fontWeight: 900, color: "#111827" },
  modalSubtitle: { fontSize: 12, color: "#6b7280", marginTop: 4 },
  closeBtn: {
    width: 38,
    height: 38,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontSize: 22,
    lineHeight: "34px",
  },
  modalBody: { padding: 18 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  field: {
    border: "1px solid #eee",
    borderRadius: 14,
    padding: 12,
    background: "#fbfbfb",
  },
  label: { fontSize: 12, color: "#6b7280", marginBottom: 6 },
  value: {
    fontSize: 14,
    color: "#111827",
    fontWeight: 800,
    wordBreak: "break-word",
  },
  valueLong: {
    fontSize: 14,
    color: "#111827",
    fontWeight: 650,
    lineHeight: 1.9,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  hint: {
    marginTop: 14,
    padding: 12,
    borderRadius: 14,
    background: "#f3f4f6",
    color: "#374151",
    fontSize: 12,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 18px 16px",
    borderTop: "1px solid #eee",
    background: "#fff",
  },
  primaryBtn: {
    border: "none",
    borderRadius: 14,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 900,
    color: "#fff",
    background: "#111827",
  },
  badgeSuccess: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#16a34a",
    color: "#fff",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 12,
    minWidth: 92,
    fontWeight: 800,
  },
  badgeDanger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#dc2626",
    color: "#fff",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 12,
    minWidth: 92,
    fontWeight: 800,
  },
};

export default HistoryInstall;
